/**
 * OnboardingWizardPage — guided first-login experience for new staff.
 *
 * Pulls /api/onboarding/me, walks the user through whichever required steps
 * are still incomplete (TOTP, Charter, Licensing per role, Licence evidence
 * PDFs, then SOPs). Hybrid gate: STRICT for required-by-role steps, FLEXIBLE
 * for SOPs (banner persists but they can navigate away).
 *
 * Post-completion: confetti + "Open dashboard" link.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, CheckCircle2, Circle, ArrowRight, ShieldCheck, ScrollText, Upload, Loader2, GraduationCap, IdCard, Lock, Trophy, FileText } from 'lucide-react';
import Header from '../components/Header';
import EvidenceUploads from '../components/EvidenceUploads';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#0EA5E9';

const STEP_META = {
  account:             { Icon: IdCard,       title: 'Account created' },
  totp:                { Icon: Lock,         title: 'Two-factor authentication',
                          help: 'Set up Google Authenticator (or any TOTP app) to protect your account.' },
  charter:             { Icon: ScrollText,   title: 'Culture Charter',
                          help: 'A short read-and-acknowledge — our practice values, expectations, escalation paths.' },
  licensing:           { Icon: ShieldCheck,  title: 'Licensing & registrations',
                          help: 'AHPRA / Radiation / ASAR numbers + expiries — DIAS 1.2 requires these on file.' },
  licensing_evidence:  { Icon: Upload,       title: 'Licence evidence PDFs',
                          help: 'Upload a screenshot or PDF of each registration. Assessors check at audit.' },
  sops_assigned:       { Icon: GraduationCap, title: 'SOPs assigned',
                          help: 'A practice manager assigns role-specific SOPs (anaphylaxis, contrast, IPC, etc.).' },
  sops_complete:       { Icon: ScrollText,    title: 'SOP read & acknowledged',
                          help: 'Read each assigned SOP and click acknowledge — counts toward your CPD.' },
};

export default function OnboardingWizardPage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/onboarding/me`, { withCredentials: true });
      setData(r.data);
      // Auto-pick the first incomplete step
      const next = r.data.steps.find(s => !s.done);
      setActive(next?.id || 'account');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load onboarding state.');
    }
    setLoading(false);
  };

  const acceptCharter = async () => {
    try {
      await axios.post(`${API}/onboarding/charter/accept`, {}, { withCredentials: true });
      await refresh?.();
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not accept Charter');
    }
  };

  const stepDone = (id) => data?.steps?.find(s => s.id === id)?.done;
  const required = useMemo(() => (data?.steps || []).filter(s => s.required), [data]);
  const requiredDone = required.filter(s => s.done).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background"><Header />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (data?.required_complete && data.complete) {
    return (
      <div className="min-h-screen bg-background" data-testid="onboarding-wizard-page">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Trophy className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h1 className="font-['Outfit'] text-3xl font-bold mb-2">All set, {user?.name}!</h1>
          <p className="text-sm text-muted-foreground mb-6">You've completed every onboarding step. Welcome to the team.</p>
          <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
            style={{ backgroundColor: C }}>
            <ArrowRight className="w-4 h-4" /> Go to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-wizard-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${C}1A`, color: C }}>
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wider">Welcome onboarding</span>
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">Welcome, {user?.name}.</h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            A quick once-off setup so we're DIAS-audit-ready from day one. Required steps must be completed before you can access the rest of the app — should take ~10 minutes.
          </p>
        </div>

        {/* Progress strip */}
        <ProgressStrip data={data} required={required} requiredDone={requiredDone} />

        {err && (
          <div className="my-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="onboarding-error">
            {err}
          </div>
        )}

        <div className="grid lg:grid-cols-[300px_1fr] gap-4 mt-4">
          {/* Step list */}
          <div className="bg-card border border-border rounded-2xl p-3 space-y-1" data-testid="onboarding-step-list">
            {data.steps.map(s => {
              const M = STEP_META[s.id] || { Icon: Circle, title: s.label };
              const isActive = active === s.id;
              const Icon = s.done ? CheckCircle2 : (isActive ? M.Icon : Circle);
              return (
                <button key={s.id} onClick={() => setActive(s.id)}
                  data-testid={`onboarding-step-${s.id}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition ${
                    isActive ? 'bg-muted' : 'hover:bg-muted/50'
                  }`}>
                  <Icon className={`w-4 h-4 shrink-0 ${s.done ? 'text-green-600' : isActive ? '' : 'text-muted-foreground'}`}
                    style={!s.done && isActive ? { color: C } : {}} />
                  <span className={`flex-1 ${s.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                    {M.title}
                  </span>
                  {s.required && !s.done && (
                    <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>Required</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Active step detail */}
          <div className="bg-card border border-border rounded-2xl p-5 min-h-[400px]" data-testid="onboarding-step-detail">
            <ActiveStep id={active} data={data} user={user} reload={load} acceptCharter={acceptCharter} />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Need help? Tap the chat bubble bottom-right at any time.
          </span>
          {data?.required_complete && (
            <Link to="/" data-testid="onboarding-skip-to-dashboard"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
              style={{ backgroundColor: C }}>
              Required steps done — open dashboard <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressStrip({ data, required, requiredDone }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 grid sm:grid-cols-3 gap-3" data-testid="onboarding-progress-strip">
      <Stat label="Overall" value={`${data.percent}%`} sub={`${data.steps.filter(s => s.done).length} of ${data.steps.length} steps`} color={data.percent === 100 ? '#16A34A' : C} />
      <Stat label="Required complete" value={`${data.required_percent}%`} sub={`${requiredDone} of ${required.length}`} color={data.required_complete ? '#16A34A' : '#D97706'} />
      <Stat label="Role" value={data.role || '—'} sub="Determines required licensing" color={C} />
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function ActiveStep({ id, data, user, reload, acceptCharter }) {
  const step = data?.steps?.find(s => s.id === id);
  const M = STEP_META[id] || {};
  if (!step) return <p className="text-sm text-muted-foreground">Pick a step on the left.</p>;

  return (
    <>
      <div className="flex items-center gap-2 mb-1">
        {step.done ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Circle className="w-4 h-4" style={{ color: C }} />}
        <h2 className="font-['Outfit'] text-lg font-semibold">{M.title || step.label}</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{M.help}</p>

      {id === 'totp' && <TOTPStep done={step.done} />}
      {id === 'charter' && <CharterStep done={step.done} accept={acceptCharter} />}
      {id === 'licensing' && <LicensingStep step={step} user={user} reload={reload} />}
      {id === 'licensing_evidence' && <EvidenceStep userId={user?.id} reload={reload} />}
      {(id === 'sops_assigned' || id === 'sops_complete') && <SOPsStep step={step} />}
      {id === 'account' && (
        <p className="text-sm text-green-700 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Your account is active. Email: <strong>{user?.email}</strong>.
        </p>
      )}
    </>
  );
}

function TOTPStep({ done }) {
  if (done) return <DoneNote text="Two-factor is enabled. Your account is protected." />;
  return (
    <div className="space-y-3 text-sm">
      <p>Open your <strong>Profile → Security</strong> page to enable Google Authenticator.</p>
      <Link to="/profile" data-testid="onboarding-totp-link"
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
        style={{ backgroundColor: C }}>
        <Lock className="w-4 h-4" /> Set up 2FA
      </Link>
    </div>
  );
}

function CharterStep({ done, accept }) {
  if (done) return <DoneNote text="Culture Charter acknowledged — thanks!" />;
  return (
    <div className="space-y-3 text-sm">
      <p>Quick read on our practice values, escalation paths, expectations, and patient-first principles.</p>
      <Link to="/culture-charter" data-testid="onboarding-charter-read"
        className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border"
        style={{ borderColor: `${C}50`, color: C }}>
        <FileText className="w-4 h-4" /> Read the Charter
      </Link>
      <p className="text-xs text-muted-foreground">After reading, click below to acknowledge:</p>
      <button onClick={accept} data-testid="onboarding-charter-accept"
        className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white"
        style={{ backgroundColor: C }}>
        <CheckCircle2 className="w-4 h-4" /> I have read &amp; acknowledge the Culture Charter
      </button>
    </div>
  );
}

function LicensingStep({ step, user, reload }) {
  if (step.done) return (
    <DoneNote text="All required licensing fields are recorded for your role.">
      <p className="text-xs text-muted-foreground mt-2">You can update these any time on your <Link to="/profile" className="underline">Profile</Link> page.</p>
    </DoneNote>
  );
  const fields = step.fields_required || [];
  return (
    <div className="space-y-3 text-sm">
      <p>Your role (<strong>{user?.role}</strong>) requires the following on file:</p>
      <ul className="space-y-1">
        {fields.map(f => {
          const present = (step.fields_present || []).includes(f);
          return (
            <li key={f} className="text-sm inline-flex items-center gap-2">
              {present ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Circle className="w-3.5 h-3.5 text-muted-foreground" />}
              <span className={present ? 'text-muted-foreground line-through' : ''}>{prettyField(f)}</span>
            </li>
          );
        })}
      </ul>
      <Link to="/profile" data-testid="onboarding-licensing-link"
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
        style={{ backgroundColor: C }}>
        <ShieldCheck className="w-4 h-4" /> Open licensing form
      </Link>
    </div>
  );
}

function EvidenceStep({ userId, reload }) {
  return (
    <div className="space-y-3 text-sm">
      <p>Upload a PDF or photo of each registration certificate (AHPRA, radiation licence, ASAR if applicable).</p>
      <EvidenceUploads parentType="user_licence" parentId={userId || ''}
        label="Licence evidence (drop in PDFs / screenshots)"
        testidPrefix="onboarding-evidence" />
      <button onClick={reload} className="text-xs underline text-muted-foreground" data-testid="onboarding-evidence-refresh">
        Refresh status after uploading
      </button>
    </div>
  );
}

function SOPsStep({ step }) {
  if (step.done) return <DoneNote text="All assigned SOPs read and acknowledged." />;
  if ((step.count ?? 0) === 0) {
    return (
      <div className="space-y-2 text-sm">
        <p>No SOPs assigned yet. Your practice manager will assign role-specific SOPs (anaphylaxis, contrast, IPC, etc.) shortly.</p>
        <p className="text-xs text-muted-foreground">This step is <strong>not blocking</strong> — feel free to use the rest of the app.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      <p>{step.count} of {step.total} assigned SOP{step.total === 1 ? '' : 's'} acknowledged.</p>
      <Link to="/sops" data-testid="onboarding-sops-link"
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl"
        style={{ backgroundColor: C }}>
        <GraduationCap className="w-4 h-4" /> Open assigned SOPs
      </Link>
    </div>
  );
}

function DoneNote({ text, children }) {
  return (
    <div className="p-3 rounded-lg border border-green-500/30 bg-green-500/5 text-sm text-green-700">
      <span className="inline-flex items-center gap-1.5">
        <CheckCircle2 className="w-4 h-4" /> {text}
      </span>
      {children}
    </div>
  );
}

function prettyField(f) {
  return ({
    ahpra_number: 'AHPRA registration number',
    ahpra_expiry: 'AHPRA expiry date',
    radiation_licence_no: 'Radiation licence number',
    radiation_licence_expiry: 'Radiation licence expiry date',
    asar_number: 'ASAR registration number (sonographer)',
    asar_expiry: 'ASAR expiry date',
  })[f] || f;
}
